"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import { Probability } from "@/components/tools/stats-distributions/charts";
import { t } from "@/lib/i18n/interpolate";
import type {
  DiscreteProbabilities,
  TableRow,
} from "@/lib/tool-kit/stats-distributions/discrete";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";

/** The kit's header cell without its upper-casing, which would turn P(X = k) into P(X = K). */
const NOTATION_TH =
  "border-border border-b px-3 py-2 text-right font-medium text-foreground text-xs";

/*
 * The parts the binomial, Poisson and hypergeometric widgets share: the six standard
 * probabilities and the full distribution table. The notation — P(X ≤ k) — is the same
 * in every language and lives here; the words beside it come from the calling widget's
 * strings.
 */

export interface MeaningLabels {
  /** Column headings. */
  probability: string;
  meaning: string;
  value: string;
  /** Plain-language readings, each with a `{k}` (or `{a}`/`{b}`) placeholder. */
  equal: string;
  atMost: string;
  lessThan: string;
  atLeast: string;
  greaterThan: string;
  between: string;
}

function Row({
  notation,
  meaning,
  value,
  fmt,
}: {
  notation: string;
  meaning: string;
  value: number;
  fmt: NumberFormatter;
}) {
  return (
    <tr>
      <td className={`${TD} whitespace-nowrap font-mono`}>{notation}</td>
      <td className={`${TD} text-muted-foreground`}>{meaning}</td>
      <td className={`${TD} text-right font-mono`}>
        <Probability fmt={fmt} value={value} />
      </td>
    </tr>
  );
}

export function ProbabilitySummary({
  probabilities,
  k,
  labels,
  fmt,
}: {
  probabilities: DiscreteProbabilities;
  k: number;
  labels: MeaningLabels;
  fmt: NumberFormatter;
}) {
  const kText = fmt(k, { maximumFractionDigits: 0 });
  const values = { k: kText };
  const range = probabilities.range;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {labels.probability}
            </th>
            <th className={TH} scope="col">
              {labels.meaning}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {labels.value}
            </th>
          </tr>
        </thead>
        <tbody>
          <Row
            fmt={fmt}
            meaning={t(labels.equal, values)}
            notation={`P(X = ${kText})`}
            value={probabilities.equal}
          />
          <Row
            fmt={fmt}
            meaning={t(labels.atMost, values)}
            notation={`P(X ≤ ${kText})`}
            value={probabilities.atMost}
          />
          <Row
            fmt={fmt}
            meaning={t(labels.lessThan, values)}
            notation={`P(X < ${kText})`}
            value={probabilities.lessThan}
          />
          <Row
            fmt={fmt}
            meaning={t(labels.atLeast, values)}
            notation={`P(X ≥ ${kText})`}
            value={probabilities.atLeast}
          />
          <Row
            fmt={fmt}
            meaning={t(labels.greaterThan, values)}
            notation={`P(X > ${kText})`}
            value={probabilities.greaterThan}
          />
          {range === null || probabilities.between === null ? null : (
            <Row
              fmt={fmt}
              meaning={t(labels.between, {
                a: fmt(range[0], { maximumFractionDigits: 0 }),
                b: fmt(range[1], { maximumFractionDigits: 0 }),
              })}
              notation={`P(${fmt(range[0], { maximumFractionDigits: 0 })} ≤ X ≤ ${fmt(range[1], { maximumFractionDigits: 0 })})`}
              value={probabilities.between}
            />
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Every k in range with its probability and both cumulative tails. */
export function DistributionTable({
  rows,
  caption,
  highlighted,
  fmt,
}: {
  rows: readonly TableRow[];
  caption: string;
  highlighted: (k: number) => boolean;
  fmt: NumberFormatter;
}) {
  return (
    <div className="max-h-96 overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
          {caption}
        </caption>
        <thead className="sticky top-0 bg-card">
          <tr>
            <th className={`${NOTATION_TH} text-left`} scope="col">
              k
            </th>
            <th className={NOTATION_TH} scope="col">
              P(X = k)
            </th>
            <th className={NOTATION_TH} scope="col">
              P(X ≤ k)
            </th>
            <th className={NOTATION_TH} scope="col">
              P(X ≥ k)
            </th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.map((row) => (
            <tr
              className={highlighted(row.k) ? "bg-primary/10" : undefined}
              key={row.k}
            >
              <th className={`${TD} font-medium`} scope="row">
                {fmt(row.k, { maximumFractionDigits: 0 })}
              </th>
              <td className={`${TD} text-right`}>
                <Probability fmt={fmt} value={row.pmf} />
              </td>
              <td className={`${TD} text-right`}>
                <Probability fmt={fmt} value={row.cdf} />
              </td>
              <td className={`${TD} text-right`}>
                <Probability fmt={fmt} value={row.upper} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
